import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { delay, STAGGER } from '@caios/ui';
import { authClient } from '../auth.ts';
import { BackupCodes, TotpEnrol } from '../components/totp-enrol.tsx';

/** Your sign-in methods and devices (ADR-004). Staff can never remove their last second factor. */
export function Security() {
  const qc = useQueryClient();
  const session = authClient.useSession();
  const passkeys = useQuery({ queryKey: ['passkeys'], queryFn: async () => (await authClient.passkey.listUserPasskeys()).data ?? [] });
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: async () => (await authClient.listSessions()).data ?? [] });
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [totpMode, setTotpMode] = useState<'idle' | 'enrol' | 'disable' | 'codes'>('idle');
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const totpOn = Boolean((session.data?.user as { twoFactorEnabled?: boolean } | undefined)?.twoFactorEnabled);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['passkeys'] }); qc.invalidateQueries({ queryKey: ['sessions'] }); session.refetch(); };
  const report = (r: { error?: { message?: string | undefined } | null } | undefined, ok: string) =>
    setMsg(r?.error ? { kind: 'err', text: r.error.message ?? 'That didn’t work.' } : { kind: 'ok', text: ok });

  async function addPasskey() {
    const r = await authClient.passkey.addPasskey({ name: `${navigator.platform || 'New'} passkey` });
    report(r, 'Passkey added.'); refresh();
  }
  async function removePasskey(id: string, name: string) {
    if (!confirm(`Remove “${name}”? You won’t be able to sign in with it again.`)) return;
    report(await authClient.passkey.deletePasskey({ id }), 'Passkey removed.'); refresh();
  }
  async function renamePasskey(id: string, current: string) {
    const name = prompt('Name this passkey', current ?? '');
    if (name) { report(await authClient.passkey.updatePasskey({ id, name }), 'Renamed.'); refresh(); }
  }
  async function passwordAction(e: FormEvent) {
    e.preventDefault();
    if (totpMode === 'disable') { report(await authClient.twoFactor.disable({ password }), 'Authenticator app removed.'); setTotpMode('idle'); }
    if (totpMode === 'codes') {
      const r = await authClient.twoFactor.generateBackupCodes({ password });
      if (r.error) report(r, ''); else setCodes(r.data?.backupCodes ?? []);
    }
    setPassword(''); refresh();
  }

  const current = session.data?.session.token;
  return (
    <div className="stack">
      <section className="cx-hero-panel page cx-rise">
        <div className="cx-eyebrow">Your account · <b>security</b></div>
        <h1 className="cx-display">How you <em>prove it’s you</em>.</h1>
        <p className="cx-body">Keep two ways in: a passkey on your computer and an authenticator app on your phone. If one is lost, the other still works.</p>
        {msg && <p className={msg.kind === 'err' ? 'auth-error' : 'ok-note'} role="status" data-testid="security-msg">{msg.text}</p>}
      </section>

      <section className="cx-panel page cx-rise" style={delay(1, STAGGER.list)}>
        <div className="cx-eyebrow">Passkeys · <b>{passkeys.data?.length ?? '…'}</b></div>
        <ul className="rows">
          {passkeys.data?.map((p) => (
            <li key={p.id} className="cx-inset row row--3" data-testid="passkey-row">
              <div><b>{p.name || 'Passkey'}</b> <span className="cx-label">added {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</span></div>
              <button type="button" className="row-action" onClick={() => renamePasskey(p.id, p.name ?? '')}>Rename</button>
              <button type="button" className="row-action" onClick={() => removePasskey(p.id, p.name ?? 'Passkey')} data-testid="passkey-remove">Remove</button>
            </li>
          ))}
        </ul>
        <button type="button" className="cx-btn" onClick={addPasskey} data-testid="passkey-add">Add a passkey</button>
      </section>

      <section className="cx-panel page cx-rise" style={delay(2, STAGGER.list)}>
        <div className="cx-eyebrow">Authenticator app · <b>{totpOn ? 'on' : 'off'}</b></div>
        {totpMode === 'enrol' && <TotpEnrol onDone={() => { setTotpMode('idle'); setMsg({ kind: 'ok', text: 'Authenticator app added.' }); refresh(); }} onCancel={() => setTotpMode('idle')} />}
        {totpMode === 'idle' && (<div className="btn-row">
          {!totpOn && <button type="button" className="cx-btn cx-btn--primary" onClick={() => setTotpMode('enrol')} data-testid="totp-add">Set up an authenticator app</button>}
          {totpOn && <button type="button" className="cx-btn" onClick={() => { setCodes([]); setTotpMode('codes'); }} data-testid="totp-codes">New backup codes</button>}
          {totpOn && <button type="button" className="cx-btn" onClick={() => setTotpMode('disable')} data-testid="totp-remove">Remove authenticator app</button>}
        </div>)}
        {(totpMode === 'disable' || (totpMode === 'codes' && !codes.length)) && (<form onSubmit={passwordAction} className="auth-form">
          <label className="field"><span className="cx-label">Confirm your password</span>
            <input type="password" autoComplete="current-password" autoFocus required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="security-password" /></label>
          <div className="btn-row">
            <button type="submit" className="cx-btn cx-btn--primary" data-testid="security-confirm">{totpMode === 'disable' ? 'Remove it' : 'Make new codes'}</button>
            <button type="button" className="cx-btn" onClick={() => setTotpMode('idle')}>Cancel</button>
          </div>
        </form>)}
        {totpMode === 'codes' && codes.length > 0 && <BackupCodes codes={codes} onDone={() => { setCodes([]); setTotpMode('idle'); }} />}
      </section>

      <section className="cx-panel page cx-rise" style={delay(3, STAGGER.list)}>
        <div className="cx-eyebrow">Signed-in devices · <b>{sessions.data?.length ?? '…'}</b></div>
        <ul className="rows">
          {sessions.data?.map((x) => (
            <li key={x.id} className="cx-inset row row--3" data-testid="session-row">
              <div><b>{describeAgent(x.userAgent)}</b> <span className="cx-label">{x.ipAddress ?? ''} · since {new Date(x.createdAt).toLocaleString()}</span></div>
              {x.token === current ? <span className="pill pill--good">this device</span>
                : <button type="button" className="row-action" onClick={async () => { report(await authClient.revokeSession({ token: x.token }), 'Signed out that device.'); refresh(); }}>Sign out</button>}
              <span />
            </li>
          ))}
        </ul>
        <button type="button" className="cx-btn" onClick={async () => { report(await authClient.revokeOtherSessions(), 'Signed out every other device.'); refresh(); }} data-testid="sessions-revoke-others">Sign out all other devices</button>
      </section>
    </div>
  );
}

function describeAgent(ua?: string | null): string {
  if (!ua) return 'Unknown device';
  const os = /Mac OS X/.test(ua) ? 'Mac' : /iPhone/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'Device';
  const br = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : /Firefox\//.test(ua) ? 'Firefox' : 'Browser';
  return `${br} on ${os}`;
}
